import { getBundles } from "app/actions/bundles.actions"
import { ShoppingCartIcon } from "components/icons/shopping-cart"
import { ShopifyBundle } from "lib/shopify/types"
import Link from "next/link"
import { useEffect, useState } from "react"
import { cn } from "utils/cn"
import { CurrencyType, mapCurrencyToSign } from "utils/mapCurrencyToSign"
import { AddToCartBundle } from "./add-to-cart-button"

const bundleItemsOrder = [
    'display-hub',
    'hubkeypad',
    'door-window-sensor',
    'motion-sensor',
    'smoke-and-heat-sensor',
    'indoor-camera',
    'outdoor-security-cameras',
]
interface BundleAddToCartItemInfoProps {
    bundleHandle: {
        value: {
            data: {
                title: string,
                cameraList: {handle: string, name: string, image: string, includedInThisPackage: string}[],
                properties: {title: string, content: string}[]
                isBundle: boolean
                bundleItems: {handle: string, content: string}[]
                handle: string
            }
        }
    },
    showPackagesButton: boolean,
    selectedCamera: {handle: string, name: string, image: string, includedInThisPackage: string},
    setSelectedCamera: (camera: {handle: string, name: string, image: string, includedInThisPackage: string}) => void
}

export function BundleAddToCartItemInfo({bundleHandle, showPackagesButton, selectedCamera, setSelectedCamera}: BundleAddToCartItemInfoProps) {
    const {title, cameraList, properties, isBundle, bundleItems, handle} = bundleHandle.value.data
    const [bundles, setBundles] = useState<ShopifyBundle[]>([])
    const [bundlesLoading, setBundlesLoading] = useState(true)
    const summary = properties.find((property: any) => property.title === 'Summary')?.content
    const description = properties.find((property: any) => property.title === 'Description')?.content
    
    useEffect(()=>{
        const fetchProductBundles = async ()=>{
            const bundles = await getBundles('product_type:bundle', 20)
            setBundles(bundles)
            setBundlesLoading(false)
        }
        fetchProductBundles()
    }, [])

    const sortBundleItems = (items: { handle: string, content: string }[]) => {
        return [...items].sort((a, b) => {
            if (a.handle === 'home-defense-kit') return 1;
            if (b.handle === 'home-defense-kit') return -1;
            
            const indexA = bundleItemsOrder.indexOf(a.handle);
            const indexB = bundleItemsOrder.indexOf(b.handle);
            return indexA - indexB;
        });
    };

    const findBundle = bundles.find((bundle: ShopifyBundle) => bundle.handle === selectedCamera.handle)
    const compareAtPrice = findBundle?.compareAtPriceRange.maxVariantCompareAtPrice.amount || 1
    const price = Number(findBundle?.priceRangeV2.minVariantPrice.amount) || 1
    const currencyCode = mapCurrencyToSign(findBundle?.priceRangeV2.minVariantPrice.currencyCode as CurrencyType) ?? ''
    const calculateSaving = (1 - (Number(price) / Number(compareAtPrice))) * 100
    return (
        <div>
            <div className="flex flex-col justify-left items-start text-sm ">
                {showPackagesButton &&
                    <div className="mb-6">
                        {description && <div className="text-[15px] font-light " dangerouslySetInnerHTML={{__html: description}}></div>}
                        {summary && <div className="font-medium text-[15px] mt-3 " dangerouslySetInnerHTML={{__html: summary}}></div>}
                    </div>
                }             
                <div className="border-b pb-4 border-[#e5e5ea]">
                    <div className="font-medium">Choose your camera</div>
                    <div className="w-full mt-3">
                        {cameraList.map((camera) => {
                            return (
                                <span className="" key={camera.handle}>
                                    <button className={cn("text-[#6b7280] border-[#6b7280] border p-1 rounded-[5px] m-1", {"bg-[#e5fbff] text-[#008eaa] border-[#008eaa]": selectedCamera.handle === camera.handle})} onClick={()=> setSelectedCamera(camera)}>
                                        <div dangerouslySetInnerHTML={{__html: camera.name}}></div>
                                    </button>
                                </span>
                            )
                        })}
                    </div>
                    <div className="mt-4 columns-2 font-light text-[15px]">
                        {sortBundleItems(bundleItems).map((item) => {                            
                            return <div className="pt-2" key={item.handle}>
                                <Link className="hover:text-fp-green"  scroll={false} href={`/products-modal/${item.handle}`} dangerouslySetInnerHTML={{__html: item.content}}></Link>
                            </div>  
                        })}
                    </div>
                </div>
            </div>
            <div className="mb-8">
                {bundlesLoading ? <BundleLoading/> :
                    <div className="mt-8 text-base flex items-center justify-center">
                        <span className="text-white bg-[#008eaa] rounded-lg px-2 py-1">Save {calculateSaving.toFixed(0)}%</span>
                        <del 
                            className="ml-4 text-[#6b7280]"
                            aria-label={`Original price: ${currencyCode}${compareAtPrice}`}
                        >
                            {currencyCode}{compareAtPrice}
                        </del>
                    </div>
                }
                {!bundlesLoading && 
                    <div className="mt-4 font-medium flex items-center justify-center text-3xl">
                        <ins 
                            className="font-bold no-underline"
                            aria-label={`Sale price: ${currencyCode}${Math.floor(price).toFixed(2)}`}
                        >
                            {currencyCode}{Math.floor(price).toFixed(2)}
                        </ins>
                    </div>
                }
                
                {selectedCamera && selectedCamera.handle && <AddToCartBundle handle={selectedCamera.handle}><ShoppingCartIcon/> Add to cart</AddToCartBundle>}
                {showPackagesButton && selectedCamera && selectedCamera.handle && <Link href={`/shop/${selectedCamera.handle}`} className="mt-4 text-center block text-fp-green font-medium underline hover:text-fp-dark-gray">SEE SYSTEM DETAILS</Link>}
            </div>
        </div>
    )
}

const BundleLoading = ()=>{
    const skeleton = 'w-full h-6 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700';

    return (
        <div className="grid grid-cols-1 gap-4 w-full mt-8 pt-4">
            <div className={skeleton} />
            <div className={skeleton} />            
        </div>
    )
}